import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Check, Plus, FileText, Upload, ListOrdered, ChevronRight } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Button } from "@/components/ui/button";
import { Stagger, StaggerItem, CountUp } from "@/components/motion/Motion";
import { useAuth } from "@/lib/auth";
import { get } from "@/lib/api";
import { STAFF } from "@/lib/nav";
import { PageHeader, Skel, InfoTip } from "@/components/app/Common";
import { NextStepBar, NextStepChip } from "@/components/app/NextStepBar";
import { JobStatusBadge, StatusChip, analysisLabel } from "@/components/app/StatusChip";
import { cn } from "@/lib/utils";
import { DashboardCharts } from "@/pages/DashboardCharts";
import { GettingStartedCard } from "@/components/app/GettingStarted";
import { InboxCard } from "@/components/app/InboxCard";

function Checklist({ checklist, canCreate, compact }) {
  const nav = useNavigate();
  const items = [
    { key: "job_created", icon: FileText, title: "Create your first job", text: "Paste a job description and let the AI draft the requirements.", cta: "Create a job", to: "/app/jobs/new", enabled: canCreate },
    { key: "resumes_added", icon: Upload, title: "Add resumes", text: "Upload resumes in bulk to start screening.", cta: "Add resumes", to: "/app/candidates", enabled: checklist.job_created },
    { key: "shortlist_reviewed", icon: ListOrdered, title: "Review your ranked shortlist", text: "See candidates ranked by Fit Score with clear reasons.", cta: "Review shortlist", to: "/app/candidates", enabled: checklist.resumes_added },
  ];
  return (
    <section className="panel p-6" data-testid="first-run-checklist">
      <h2 className="text-lg font-semibold text-ink">{compact ? "Finish getting started" : "Get started in three steps"}</h2>
      <ol className="mt-4 grid gap-3 md:grid-cols-3">
        {items.map((it, i) => {
          const done = checklist[it.key];
          return (
            <li key={it.key} data-testid={`checklist-step-${i + 1}`} className={cn("flex flex-col gap-3 rounded-[6px] border p-4", done ? "border-[#CADBCF] bg-success-tint" : "border-line bg-subtle")}>
              <div className="flex items-center gap-2">
                <span data-testid={`checklist-step-${i + 1}-status`} className={cn("flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold", done ? "bg-success text-white" : "border border-line bg-white text-ink-secondary")}>
                  {done ? <Check size={14} /> : i + 1}
                </span>
                <p className="text-sm font-medium text-ink">{it.title}</p>
              </div>
              <p className="text-sm text-ink-secondary">{it.text}</p>
              {!done && (
                <Button variant="outline" size="sm" className="mt-auto self-start" disabled={!it.enabled} data-testid={`checklist-step-${i + 1}-btn`} onClick={() => nav(it.to)}>
                  {it.cta}
                </Button>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

const Kpi = ({ label, value, decimals = 0, tip, testId, suffix }) => (
  <StaggerItem className="rounded-[6px] bg-white/70 p-4 ring-1 ring-[#1F4D3A]/10" data-testid={testId} data-value={value ?? ""}>
    <p className="flex items-center gap-1.5 text-sm text-ink-secondary">{label}{tip && <InfoTip text={tip} />}</p>
    <CountUp value={value} decimals={decimals} suffix={suffix} className="mt-2 block font-display text-3xl font-semibold tracking-[-0.02em] text-ink tabular-nums" />
  </StaggerItem>
);

function KpiRow({ d }) {
  return (
    <section className="tl-hero p-5 sm:p-6" data-testid="kpi-hero">
      <p className="mb-4 text-[11px] font-medium uppercase tracking-[0.12em] text-ink-muted">Hiring at a glance</p>
      <Stagger className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-tour="pt-kpi-row" data-testid="kpi-row">
        <Kpi label="Open jobs" value={d.open_jobs} testId="kpi-open-jobs" />
        <Kpi label="Applicants this month" value={d.applicants_this_month} testId="kpi-applicants" />
        <Kpi label="Awaiting action" value={d.awaiting_action} tip="Jobs whose AI interpretation needs your review." testId="kpi-awaiting" />
        <Kpi label="Avg Fit Score" value={d.avg_fit_score != null ? Number(d.avg_fit_score) : null} decimals={1} tip="Average Fit Score (0–10) across scored candidates, calculated in code from every completed stage." testId="kpi-avg-fit" />
        <Kpi label="Median time to shortlist" value={d.median_time_to_shortlist_days} decimals={1} suffix=" days"
          tip={d.median_time_to_shortlist_days == null ? "No one has been shortlisted from applications in the last 90 days yet. This fills in after the first candidate is shortlisted." : `Median days from applying to first being shortlisted, across ${d.median_time_to_shortlist_n} application(s) from the last 90 days.`} testId="kpi-time-to-shortlist" />
      </Stagger>
    </section>
  );
}

function RecentJobs({ jobs }) {
  return (
    <section className="panel" data-tour="pt-recent-jobs" data-testid="recent-jobs">
      <div className="flex items-center justify-between max-sm:flex-wrap max-sm:gap-2 border-b border-line px-6 py-4">
        <h2 className="text-base font-semibold">Recent jobs</h2>
        <Link to="/app/jobs" className="text-sm text-brand hover:underline">View all</Link>
      </div>
      <Stagger as="ul" className="divide-y divide-line">
        {jobs.map((j) => (
          <StaggerItem as="li" key={j.id}>
            <Link to={j.status === "draft" ? `/app/jobs/new?draft=${j.id}` : `/app/jobs/${j.id}/workspace`} data-testid={`recent-job-${j.id}`} className="tl-row flex items-center gap-4 px-6 py-3">
              <span className="flex-1 truncate text-sm font-medium text-ink">{j.title || "Untitled job"}</span>
              <StatusChip status={analysisLabel(j.analysis_status)} />
              <JobStatusBadge status={j.status} />
              <span className="w-28 text-right text-xs text-ink-muted">{formatDistanceToNow(new Date(j.updated_at), { addSuffix: true })}</span>
              <ChevronRight size={16} className="tl-chev text-ink-muted" />
            </Link>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}

function nextStep(d, canCreate) {
  if (!d) return { loading: true };
  if (d.total_jobs === 0) return canCreate ? { text: "Create your first job", to: "/app/jobs/new", label: "Create a job" } : { text: "Waiting for a recruiter to create the first job" };
  if (d.awaiting_action > 0) return { text: `${d.awaiting_action} job${d.awaiting_action > 1 ? "s" : ""} need your review`, to: "/app/jobs", label: "Review jobs" };
  if (d.open_jobs === 0) return { text: "Finish and publish a draft job", to: "/app/jobs", label: "Go to jobs" };
  return { text: "Add resumes to your open jobs to start screening", to: "/app/jobs", label: "Go to jobs" };
}

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const canCreate = STAFF.includes(user.role);
  const { data, isLoading } = useQuery({ queryKey: ["dashboard"], queryFn: () => get("/dashboard/summary") });
  const step = nextStep(data, canCreate);

  return (
    <div className="space-y-8" data-tour="dashboard" data-testid="dashboard-page">
      <PageHeader
        title={`Welcome, ${user.name.split(" ")[0]}`}
        subtitle="Here's where hiring stands today."
        actions={<>
          <NextStepChip />
          {canCreate && (
            <Button data-tour="create-job" data-testid="dashboard-create-job-btn" onClick={() => nav("/app/jobs/new")}>
              <Plus size={16} /> Create job
            </Button>
          )}
        </>}
      />
      <InboxCard />
      <GettingStartedCard />
      <NextStepBar loading={step.loading} text={step.text} actionLabel={step.label} onAction={() => step.to && nav(step.to)} />
      {isLoading ? (
        <div className="grid gap-4 lg:grid-cols-5">{Array.from({ length: 5 }).map((_, i) => <Skel key={i} className="h-28" />)}</div>
      ) : data.total_jobs === 0 ? (
        <Checklist checklist={data.checklist} canCreate={canCreate} />
      ) : (
        <>
          <KpiRow d={data} />
          <DashboardCharts />
          <RecentJobs jobs={data.recent_jobs} />
        </>
      )}
    </div>
  );
}
